import React, { useEffect, useState, useCallback } from "react";
import LoginForm from "./LoginForm";
import Homepage_Post from "./Homepage_Post";
import "../styles/Homepage.css";
import FilterModal from "./FilterModal";

const HomePage = ({ isLoggedIn, setLoggedIn, doRefreshPosts, appRefresh }) => {
  // State variables
  const [userToken, setUserToken] = useState("");
  const [showFilterModal, setShowFilterModal] = useState(false);
  const [selectedFilters, setSelectedFilters] = useState([]);
  const [filteredPosts, setFilteredPosts] = useState([]);
  const [isFiltering, setIsFiltering] = useState(false);
  const [allPosts, setAllPosts] = useState([]);
  const [dishesData, setDishesData] = useState(null);
  const [suggestedDishes, setSuggestedDishes] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [isFollow, setIsFollowing]=useState(false);
  const [isLatest, setIsLatest]=useState(false);
  const [isGood, setIsGood]=useState(false);
  const [isBad, setIsBad]=useState(false);
  const [isYou, setIsYou] = useState(false);
  // Function to clear all filters
  const clearAllFilters = useCallback(() => {
    setSelectedFilters([]);
    setIsFiltering(false);
    setFilteredPosts([]);
    setSuggestedDishes([]);
    doRefreshPosts();
  }, [doRefreshPosts]);

  
  const sorting = (event) => {
    const value = event.target.value;
    if (value === "latest") {
      setIsLatest(true);
      setIsFollowing(false);
      setIsBad(false);
      setIsYou(false);
      setIsGood(false);
    } else if (value === "following") {
      setIsFollowing(true);
      setIsLatest(false);
      setIsBad(false);
      setIsYou(false);
      setIsGood(false);
    }
    else if (value=== "best"){
      setIsGood(true);
      setIsLatest(false);
      setIsBad(false);
      setIsYou(false);
      setIsFollowing(false);
    }
    else if (value=== "worst"){
      setIsBad(true);
      setIsLatest(false);
      setIsGood(false);
      setIsYou(false);
      setIsFollowing(false);
    }
    else if (value=== "foryou"){
      setIsYou(true);
      setIsBad(false);
      setIsLatest(false);
      setIsGood(false);
      setIsFollowing(false);
    }
  };

  // Expose clearAllFilters to window for Sidebar to access
  useEffect(() => {
    window.clearHomeFilters = clearAllFilters;
    return () => {
      delete window.clearHomeFilters;
    };
  }, [clearAllFilters]);

  // Set user token when component loads
  useEffect(() => {
    setUserToken(sessionStorage.getItem("token"));
    
    // Load dishes data
    const loadDishesData = async () => {
      try {
        const dishes = await import('../assets/dishes2.json');
        setDishesData(dishes.default);
        // Set initial loading to false once data is loaded
        setInitialLoading(false);
      } catch (error) {
        console.error("Error loading dishes data:", error);
        setInitialLoading(false);
      }
    };
    
    loadDishesData();
  }, []);

  // Function to toggle filter modal visibility
  const toggleFilterModal = () => {
    setShowFilterModal(!showFilterModal);
  };

  // Function to filter dishes based on selected filters
  const filterDishes = (filters) => {
    if (!dishesData || !filters || filters.length === 0) {
      return [];
    }

    // Convert filters to lowercase for case-insensitive comparison
    const lowerCaseFilters = filters.map(f => f.toLowerCase());
    
    // Filter dishes that match ALL selected filters
    return dishesData.filter(dish => {
      if (!dish.tags || dish.tags.length === 0) return false;
      
      // Check if dish tags match ALL user filters
      return lowerCaseFilters.every(filter => 
        dish.tags.some(tag => tag.toLowerCase() === filter)
      );
    });
  };

  // Function to handle filter application
  const applyFilters = (filters) => {
    setSelectedFilters(filters);
    
    // If we have an empty filter array, reset to show all posts
    if (filters.length === 0) {
      setIsFiltering(false);
      setSuggestedDishes([]);
      doRefreshPosts(); // Reset to show all posts
      return;
    }
    
    setIsFiltering(true);
    setIsLoading(true); // Show loading indicator

    // Get all posts first, then filter client-side
    const token = sessionStorage.getItem("token");
    fetch(`${process.env.REACT_APP_API_PATH}/posts?parentID=`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        ...(token && { "Authorization": `Bearer ${token}` })
      }
    })
      .then(res => {
        if (!res.ok) {
          throw new Error(`API error: ${res.status} ${res.statusText}`);
        }
        return res.json();
      })
      .then(async (result) => {
        if (result && result[0]) {
          const posts = result[0];
          console.log("Successfully fetched posts for filtering:", posts.length);
          
          // Process posts to add dish tags from dishes2.json
          const processedPosts = await Promise.all(posts.map(async (post) => {
            // If post has dishID, find matching dish and add its tags
            if (post.attributes?.dishID && dishesData) {
              const matchingDish = dishesData.find(dish => 
                dish.id === Number(post.attributes.dishID)
              );
              
              if (matchingDish) {
                // Add dish tags directly to the post for filtering
                return {
                  ...post,
                  dishTags: matchingDish.tags || []
                };
              }
            }
            
            // Return the post unchanged if no dishID or no matching dish
            return {
              ...post,
              dishTags: []
            };
          }));
          
          // Now filter the posts with the processed data
          const filtered = processedPosts.filter(post => {
            const lowerCaseFilters = filters.map(f => f.toLowerCase());
            
            // Check if ALL filters match (not just one)
            return lowerCaseFilters.every(filter => {
              // Helper function to check if a word matches the filter
              const isWholeWordMatch = (text, searchWord) => {
                if (!text) return false;
                const words = text.toLowerCase().split(/\s+/);
                return words.includes(searchWord);
              };

              // 1. Check post content and title
              const contentMatch = 
                isWholeWordMatch(post.content, filter) ||
                isWholeWordMatch(post.attributes?.title, filter);
              
              // 2. Check post attributes tags
              const postTags = post.attributes?.tags || [];
              const tagMatch = postTags.length > 0 && 
                postTags.some(tag => tag.toLowerCase() === filter);
              
              // 3. Check dishTags from the matching dish in dishes2.json
              const dishTagMatch = post.dishTags.length > 0 &&
                post.dishTags.some(tag => tag.toLowerCase() === filter);
              
              return contentMatch || tagMatch || dishTagMatch;
            });
          });
          
          console.log("Filtered posts:", filtered.length);
          setFilteredPosts(filtered);
          
          // If no filtered posts found, find dish suggestions that match the filters
          if (filtered.length === 0) {
            const matchingDishes = filterDishes(filters);
            console.log("Found matching dishes:", matchingDishes.length);
            setSuggestedDishes(matchingDishes);
          } else {
            setSuggestedDishes([]);
          }
        } else {
          console.error("Invalid response format:", result);
          setFilteredPosts([]);
          
          // Try to find dish suggestions if no posts are found
          const matchingDishes = filterDishes(filters);
          setSuggestedDishes(matchingDishes);
        }
      })
      .catch(error => {
        console.error("Error filtering posts:", error);
        setIsFiltering(false); // Reset filtering state on error
        setSuggestedDishes([]);
        
      })
      .finally(() => {
        setIsLoading(false); // Hide loading indicator when done
      });
  };

  // Helper to determine if we should show loading state
  const isLoadingState = initialLoading || isLoading;

  return (
    <div className="homepage-container">
      <head>
        <link rel="preconnect" href="https://fonts.googleapis.com"></link>
        <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin></link>
        <link href="https://fonts.googleapis.com/css2?family=Fredericka+the+Great&family=Fredoka:wght@500&family=Urbanist:ital,wght@0,500;1,500&display=swap" rel="stylesheet"></link>
      </head>
      
     

      {/* Filter Button */}
      {isLoadingState && (
        <div className="loading-indicator">
          <div className="loading-spinner"></div>
          <p>{isLoading ? "Loading filtered posts..." : "Loading content..."}</p>
        </div>
      )}

      {/* Main Content - Only render when not in initial loading */}
      {!isLoadingState && (
        <div className="homebox">

      <div className="sorting">
      <div className="filter-button-container">
      <select className="sort" id="sort" onChange={sorting}  disabled={isFiltering}>
  <option value="latest">Latest</option>
  {userToken && <option value="following">Following</option>}
  {userToken && <option value="foryou">For you</option>}
  <option value="best">Best</option>
  <option value="worst">Worst</option>
</select>
        <button 
          className={`filter-button ${selectedFilters.length > 0 ? 'has-filters' : ''}`} 
          onClick={toggleFilterModal}
          aria-label="Filter posts"
        >
          {selectedFilters.length > 0 ? `Filters (${selectedFilters.length})` : "Filter"}
        </button>
      
      </div>
      </div>
      <br></br>
      
      {/* Filter Modal */}
      {showFilterModal && (
        <FilterModal 
          onClose={toggleFilterModal} 
          onApply={applyFilters}
          initialFilters={selectedFilters}
        />
      )}
      
      {/* Loading Indicator */}
      {isLoading && (
        <div className="loading-indicator">
          <div className="loading-spinner"></div>
          <p>Loading filtered posts...</p>
        </div>
      )}
      
      {/* Homepage Posts - pass filtered posts if filtering is active */}
      <Homepage_Post 
        doRefreshPosts={doRefreshPosts} 
        appRefresh={appRefresh}
        filteredPosts={isFiltering ? filteredPosts : null}
        suggestedDishes={isFiltering ? suggestedDishes : [] }
        follow={isFollow}
        late = {isLatest}
        good = {isGood}
        bad = {isBad}
        you = {isYou}
      />
      </div>
    )}
    </div>
  );
};

export default HomePage;

